/**
 * DealProgressTimeline — live per-stage timeline of the Deal Processing
 * Engine pipeline.
 *
 * Subscribes via `useDealProgressStream(dealAnalysisId)` (SSE). The hook
 * holds an EventSource against the deal-progress-stream Lambda and
 * forwards each stage transition as it lands; on reconnect it replays
 * from `DealAnalysis.progressEvents` so the timeline survives
 * tab-backgrounded → resumed without missing stages. This component is
 * intentionally agnostic to the transport — it just renders whatever the
 * hook delivers.
 *
 * Visual model: one row per stage transition (`started` → `completed` /
 * `failed`). The row carries Ari's narration as primary copy with the
 * stage label + a coarse status pip. While a stage is in flight the
 * pip pulses; on completion it locks to green; on failure red.
 */

import { CheckCircle2, Loader2, Circle, AlertCircle } from "lucide-react"
import { useDealProgressStream } from "@/hooks/deal/useDealProgressStream"
import type { DealProgressEvent } from "src/api/deal-engine/DealEngine"
import { cn } from "src/utils/Functions"

export interface DealProgressTimelineProps {
  /** When null, the component renders an empty placeholder (deal hasn't been pushed yet). */
  dealAnalysisId: string | null
  /**
   * Snapshot status from `/deals/get`'s `latestAnalysis.status` — seeds
   * the strip-header pill so already-complete deals don't flash "Running"
   * for ~15s while the SSE replay catches up on initial mount.
   */
  initialStatus?: string
  className?: string
}

const STAGE_LABEL: Record<string, string> = {
  SUMMARY: "Reading the contract",
  STAKEHOLDER_ENRICHMENT: "Linking parties",
  EQ_ANALYSIS: "Identifying terms",
  CARD_CREATION: "Building EQ cards",
  IQ_ANALYSIS: "Running IQ legal analysis",
  VARIABLE_EXTRACTION: "Extracting variables",
  CONTRACT_MARKUP: "Marking up the contract",
  TTS_PROMPT_GENERATION: "Drafting audio prompts",
  TTS_AUDIO_GENERATION: "Generating audio",
}

function statusIcon(status: string, isLatest: boolean) {
  if (status === "completed") {
    return <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
  }
  if (status === "failed") {
    return <AlertCircle className="size-4 text-rose-500 shrink-0" />
  }
  if (isLatest && (status === "started" || status === "in_progress")) {
    return (
      <Loader2 className="size-4 text-blue-500 animate-spin shrink-0" />
    )
  }
  return <Circle className="size-4 text-neutral-400 shrink-0" />
}

function StageRow({
  event,
  isLatest,
}: {
  event: DealProgressEvent
  isLatest: boolean
}) {
  return (
    <div
      className="flex gap-3 items-start py-2"
      data-testid={`deal-progress-stage-${event.stage}`}
      data-stage={event.stage}
      data-stage-status={event.status}
    >
      <div className="pt-0.5">{statusIcon(event.status, isLatest)}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-neutral-900 dark:text-neutral-100 leading-snug">
          {event.narration ??
            STAGE_LABEL[event.stage] ??
            event.stage}
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 flex gap-2">
          <span className="font-medium">
            {STAGE_LABEL[event.stage] ?? event.stage}
          </span>
          <span>·</span>
          <span>{new Date(event.at).toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  )
}

export function DealProgressTimeline({
  dealAnalysisId,
  initialStatus,
  className,
}: DealProgressTimelineProps) {
  const { events, status, polling } = useDealProgressStream(
    dealAnalysisId,
    initialStatus,
  )

  if (!dealAnalysisId) {
    return (
      <div
        className={cn(
          "rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700 p-4 text-sm text-neutral-500",
          className,
        )}
      >
        Upload a contract to begin the analysis pipeline.
      </div>
    )
  }

  return (
    <div
      data-testid="deal-progress-timeline"
      data-status={status}
      data-polling={polling ? 'true' : 'false'}
      className={cn(
        "rounded-xl border border-border dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden",
        className,
      )}
    >
      <div className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800 border-b border-border dark:border-neutral-800 flex items-center justify-between">
        <div className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
          Analysis pipeline
        </div>
        <StatusPill status={status} polling={polling} />
      </div>
      <div className="px-4 py-2 max-h-80 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800">
        {events.length === 0 ? (
          <div className="text-xs text-neutral-500 py-4">
            Waiting for the engine to start…
          </div>
        ) : (
          events.map((evt, idx) => (
            <StageRow
              key={`${evt.stage}-${evt.at}-${idx}`}
              event={evt}
              isLatest={idx === events.length - 1}
            />
          ))
        )}
      </div>
    </div>
  )
}

function StatusPill({ status, polling }: { status: string; polling: boolean }) {
  const label =
    status === "complete"
      ? "Complete"
      : status === "failed"
        ? "Failed"
        : status === "running" || polling
          ? "Running"
          : status === "pending"
            ? "Queued"
            : "Idle"

  const tone =
    status === "complete"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
      : status === "failed"
        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
        : status === "running" || polling
          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400"

  return (
    <span
      data-testid="deal-progress-status-pill"
      data-status-label={label}
      className={cn(
        "px-2 py-0.5 text-xs font-medium rounded-full",
        tone,
      )}
    >
      {label}
    </span>
  )
}
